// src/pages/PostDetailsPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import PostCard from '../components/PostCard';
import PostCardSkeleton from '../components/PostCardSkeleton';
import CommentSection from '../components/CommentSection';
import styles from './PostDetailsPage.module.css';
import { useTranslation } from 'react-i18next';
import { useUser } from '../context/UserContext'; // Assuming context is needed for PostCard props
import { deletePost } from '../services/postService';

const PostDetailsPage = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { t } = useTranslation();
  const { currentUser, firebaseUser } = useUser();

  const isModerator = ['admin', 'editor', 'owner'].includes(currentUser?.role);

  const canDeletePost = (post) => {
    if (!post) return false;
    if (isModerator) return true;
    return firebaseUser?.uid === post.userId;
  };

  const handleDeletePost = async (post) => {
    if (!canDeletePost(post)) return;
    const confirmed = window.confirm(t('community.posts.confirmDelete') || 'Are you sure you want to delete this post?');
    if (!confirmed) return;

    try {
      await deletePost(post.id, post.imagePath);
      navigate('/community');
    } catch (err) {
      console.error('Failed to delete post:', err);
      alert(t('community.posts.errors.delete') || 'Failed to delete post');
    }
  };

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const docRef = doc(db, 'posts', postId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setPost({ id: docSnap.id, ...docSnap.data() });
        } else {
          setError('Post not found');
        }
      } catch (err) {
        console.error(err);
        setError('Error loading post: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    if (postId) {
      fetchPost();
    }
  }, [postId]);

  if (loading) {
    return (
      <div className={styles.detailPage}>
        <div className={styles.topBar}>
          <button onClick={() => navigate(-1)} className="backButton">
            &larr; {t('searchResults.back')}
          </button>
        </div>
        <PostCardSkeleton />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className={styles.detailPage}>
        <div className={styles.error}>{error || 'Post not found'}</div>
            <button onClick={() => navigate('/community')} className="backButton">
              &larr; {t('searchResults.visitCommunity')}
        </button>
      </div>
    );
  }

  return (
    <div className={styles.detailPage}>
      <div className={styles.topBar}>
        <button onClick={() => navigate(-1)} className="backButton">
          &larr; {t('searchResults.back')}
        </button>
      </div>
      
      <PostCard 
        post={post} 
        isDetailView={true}
        currentUserId={firebaseUser?.uid}
        canDelete={canDeletePost(post)}
        onDelete={() => handleDeletePost(post)}
        onToggleLike={() => {}} // TODO: Implement single post like logic
      />

      <CommentSection postId={postId} />
    </div>
  );
};

export default PostDetailsPage;
